/**
 * tutorLiveRoomViewModel — la salle du tuteur, de l'état du moteur (`useTutorLive`) aux props de
 * `<TutorLivePage>` qui la regardent : ce qui tient la place de la salle (`notice`) et la boîte que la
 * salle pose par-dessus elle, une à la fois. Pur, sans Redux : son entrée est l'état du moteur, comme
 * `studentLiveViewModel` pour l'élève.
 */
import { type RoomConnection } from "../../../../../engine/live/room/roomMachine"
import { type UITutorLiveDialog } from "../../../../../design-system/templates/TutorLiveOverlays/TutorLiveOverlays"
import { type UITutorLiveNotice } from "../../../ui/types/tutorLive"

export type TutorLiveRoomNoticeInput = {
    /** La séance est dans le compte (`live_sessions`). */
    sessionKnown: boolean
    connection: RoomConnection
    /** La caméra et le micro ont reçu leur réponse. */
    mediaChecked: boolean
    /** Au web, la question de la caméra et du micro attend toujours sa réponse, passé le délai. */
    mediaWaiting: boolean
    enrolledCount: number
    presentCount: number
    /** Trois minutes passées depuis le début de la séance, salle non `trial`. */
    noStudentYetDue: boolean
}

/**
 * Ce que la page dit à la place de la salle (`TutorLiveScreen.js:306-381`, `LiveContainerV3.js:92-97`) :
 *  - la séance pas encore dans le compte, la salle pas connectée, ou la réponse du navigateur à la
 *    demande de la caméra et du micro pas encore reçue : « Connexion… » (l'ancien laissait un fond vide
 *    dans ce dernier cas, S-62) ; une reprise : « Reconnexion… » ;
 *  - la question de la caméra et du micro sans réponse passé le délai (au web) : « media_pending », qui dit où
 *    répondre. Sans lui, un navigateur dont la bulle restait ouverte laissait « Connexion… » sans fin, salle
 *    connectée (staging, 03/11) ;
 *  - connectée : « Aucun élève inscrit » sans élève ; « Aucun élève connecté » quand aucun n'est là trois
 *    minutes après le début ;
 *  - sinon rien : la salle.
 */
export function tutorLiveRoomNotice(input: TutorLiveRoomNoticeInput): UITutorLiveNotice | undefined {
    if (!input.sessionKnown) return "connecting"
    if (input.connection === "reconnecting") return "reconnecting"
    if (!input.mediaChecked && input.mediaWaiting) return "media_pending"
    if (input.connection !== "connected" || !input.mediaChecked) return "connecting"
    if (input.enrolledCount === 0) return "no_student_enrolled"
    if (input.presentCount === 0 && input.noStudentYetDue) return "no_student_yet"
    return undefined
}

/** Les boîtes de la salle, de la plus forte à la plus faible ; l'écran les range avec celles des autres zones. */
export const TUTOR_ROOM_DIALOG_PRIORITY = {
    /** Le relevé fait sortir : rien d'autre ne compte. */
    exit: 100,
    /** Un autre onglet tient la séance : celui-ci ne doit rien faire avant de choisir. */
    keep_tab: 90,
    /** Une notification touchée d'une autre séance. */
    tap: 60,
    /** Le live suivant a commencé. */
    overrun: 50,
    /** La caméra ou le micro refusés au navigateur. */
    permissions: 40,
} as const

export type TutorRoomDialogKey = keyof typeof TUTOR_ROOM_DIALOG_PRIORITY

/**
 * Les boîtes de la salle qui s'ouvrent sans geste du masteur et parlent d'élèves (`TutorLiveLayer.dialogAutomatic`) :
 * pendant une présentation, recadrée ou non, elles se poseraient sur le centre qui part chez les élèves : elles
 * attendent qu'elle finisse.
 *  - `overrun` : le relevé dit que le live suivant a commencé, avec les prénoms de ses élèves ;
 *  - `tap` : une notification d'une autre séance (un message, un appel), touchée hors de la salle.
 * Ne s'y rangent pas : la sortie forcée (`exit`, elle doit rester visible), l'autre onglet (`keep_tab`, rien ne
 * se fait ici avant de choisir) et les permissions refusées (`permissions`, à l'entrée, sans prénom).
 */
export const TUTOR_ROOM_AUTOMATIC_DIALOGS: ReadonlySet<TutorRoomDialogKey> = new Set<TutorRoomDialogKey>([
    "overrun",
    "tap",
])

export function isTutorRoomDialogAutomatic(key: TutorRoomDialogKey): boolean {
    return TUTOR_ROOM_AUTOMATIC_DIALOGS.has(key)
}

export type TutorRoomDialogCandidates = Partial<Record<TutorRoomDialogKey, UITutorLiveDialog | null>>

/** La boîte que la salle montre : la plus forte des candidates, avec sa priorité. */
export function pickTutorRoomDialog(
    candidates: TutorRoomDialogCandidates
): { key: TutorRoomDialogKey; value: UITutorLiveDialog; priority: number } | null {
    const keys = (Object.keys(TUTOR_ROOM_DIALOG_PRIORITY) as TutorRoomDialogKey[]).sort(
        (a, b) => TUTOR_ROOM_DIALOG_PRIORITY[b] - TUTOR_ROOM_DIALOG_PRIORITY[a]
    )
    for (const key of keys) {
        const value = candidates[key]
        if (value != null) return { key, value, priority: TUTOR_ROOM_DIALOG_PRIORITY[key] }
    }
    return null
}
